import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { API_URL } from '../config/api';
import './AdminLogin.css';

function AdminLoginForm() {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Try to get reCAPTCHA token if available, otherwise proceed without it
      let captchaToken = null;
      if (executeRecaptcha) {
        try {
          captchaToken = await executeRecaptcha('login');
        } catch (captchaErr) {
          console.warn('reCAPTCHA failed, proceeding without it:', captchaErr);
        }
      }

      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          ...(captchaToken && { captchaToken })
        }),
      });

      const data = await response.json();

      if (response.ok || data.token) {
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminUser', JSON.stringify(data.admin));
        navigate('/admin/dashboard');
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      setError('Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="admin-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7v13l10 5 10-5V7L12 2z"></path>
                <path d="M12 22V12"></path>
                <path d="M12 12L2 7"></path>
                <path d="M12 12l10-5"></path>
              </svg>
            </div>
            <h1>Admin Panel</h1>
            <p>Sign in to access the dashboard</p>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="info@flightreservationcentre.com"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="forgot-password-link">
              <a href="/admin/forgot-password">Forgot Password?</a>
            </div>

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="login-footer">
            <p>Protected Area - Authorized Access Only</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminLogin() {
  return (
    <GoogleReCaptchaProvider 
      reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LdAqRwsAAAAAN9qG-8xFI0uFUdepH7ZCo1L6zzS"}
    >
      <AdminLoginForm />
    </GoogleReCaptchaProvider>
  );
}

export default AdminLogin;
